import { startTransition, useRef, useState, type ChangeEvent, type SubmitEvent } from 'react';
import { useFormStatus } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CancelOutlined, CheckCircleOutline } from '@mui/icons-material';

import { PasswordField } from '@/components/common/PasswordField';
import { usePasswordValidation } from '@/hooks/usePasswordValidation';
import { useNotificationStore } from '@/stores/notificationstore';
import { type State } from '@/types';
import { PasswordStrengthFeedback } from '@/components/common/PasswordStrengthFeedback';
import { PasswordChangePageTitle, PasswordChangeSubmitButton } from './PasswordChange.style';
import { getPasswordStrengthColor } from '@/components/common/passwordStrength';
import { getApiErrorMessageKey } from '@/shared/api/apiError';
import type { ChangePasswordFields } from './ChangePasswordPage';
import { FrontPage } from './FrontPage';

function getPasswordConfirmationColor(isAccepted: boolean, passwordsMatch: boolean, validationColor: string) {
  if (!isAccepted) {
    return 'text.disabled';
  }
  if (!passwordsMatch) {
    return 'error.main';
  }
  return validationColor;
}

function Submit({ disabled }: { disabled: boolean }) {
  const { pending } = useFormStatus();
  const { t } = useTranslation();

  return (
    <PasswordChangeSubmitButton type="submit" fullWidth variant="contained" disabled={pending || disabled}>
      {t('login.resetPassword.passwordChangeButton')}
    </PasswordChangeSubmitButton>
  );
}

function ChangePassword({
  state,
  formAction,
  isPhone,
  username,
  sudoPassword,
}: {
  state: State<ChangePasswordFields>;
  formAction: (payload: ChangePasswordFields) => void;
  isPhone: boolean;
  username: string;
  sudoPassword: string;
}) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const showNotification = useNotificationStore((state) => state.showNotification);
  const ref = useRef<HTMLFormElement | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordAgain, setNewPasswordAgain] = useState('');
  const language = i18n.language;
  const {
    isAccepted: isPasswordAccepted,
    isPending: isValidationPending,
    validation,
  } = usePasswordValidation({
    username,
    newPassword,
    sudoPassword,
    language,
    onError: (error) => {
      const messageKey = getApiErrorMessageKey(error);
      if (messageKey) {
        showNotification('error', t(messageKey));
      }
    },
  });
  const progressValue = Math.min(120, Math.max(0, validation?.strength ?? 0));
  const validationColor = getPasswordStrengthColor(theme, progressValue, isPasswordAccepted);
  const passwordsMatch = newPasswordAgain === newPassword;
  const isPasswordConfirmationValid = newPasswordAgain.length > 0 && passwordsMatch;
  const passwordConfirmationColor = getPasswordConfirmationColor(isPasswordAccepted, passwordsMatch, validationColor);

  function handlePasswordChange(event: ChangeEvent<HTMLInputElement>) {
    setNewPassword(event.target.value);
  }

  function handlePasswordAgainChange(event: ChangeEvent<HTMLInputElement>) {
    setNewPasswordAgain(event.target.value);
  }

  function handleSubmit(event: SubmitEvent<HTMLFormElement> | undefined) {
    event?.preventDefault();
    if (!isPasswordAccepted || !isPasswordConfirmationValid) {
      return;
    }
    const form = ref.current;
    if (!form) {
      return;
    }
    const formData = new FormData(form);
    const formPayload: ChangePasswordFields = {
      newPassword: String(formData.get('newPassword') ?? ''),
      newPasswordAgain: String(formData.get('newPasswordAgain') ?? ''),
    };

    startTransition(() => formAction(formPayload));
  }

  return (
    <>
      {!isPhone && (
        <PasswordChangePageTitle variant="h3">{t('login.resetPassword.passwordChange')}</PasswordChangePageTitle>
      )}

      <Box component="form" ref={ref} onSubmit={handleSubmit} noValidate>
        <PasswordField
          name="newPassword"
          labelKey="login.password"
          error={state.errors.newPassword}
          autoFocus={true}
          autoComplete="new-password"
          disabled={!sudoPassword}
          onChange={handlePasswordChange}
        />
        <PasswordStrengthFeedback
          newPassword={newPassword}
          validation={validation}
          isPending={isValidationPending}
          validationColor={validationColor}
        />
        <Stack spacing={0.5}>
          <PasswordField
            name="newPasswordAgain"
            labelKey="login.resetPassword.passwordAgain"
            error={state.errors.newPasswordAgain}
            autoComplete="new-password"
            disabled={!isPasswordAccepted}
            onChange={handlePasswordAgainChange}
          />
          {newPasswordAgain && (
            <Stack
              direction="row"
              sx={{ alignItems: 'center', gap: 0.5 }}
              data-qa-element-id="password-confirmation-status"
            >
              {passwordsMatch ? (
                <CheckCircleOutline sx={{ color: passwordConfirmationColor, fontSize: 16 }} />
              ) : (
                <CancelOutlined sx={{ color: passwordConfirmationColor, fontSize: 16 }} />
              )}
              <Typography variant="caption" sx={{ color: passwordConfirmationColor }}>
                {t(
                  passwordsMatch
                    ? 'login.resetPassword.passwordConfirmationMatches'
                    : 'login.resetPassword.passwordConfirmationDoesNotMatch'
                )}
              </Typography>
            </Stack>
          )}
        </Stack>

        <Submit disabled={!isPasswordAccepted || !isPasswordConfirmationValid} />
      </Box>
    </>
  );
}

export function ChangePasswordForm({
  state,
  formAction,
  isPhone,
  username,
  sudoPassword,
}: {
  state: State<ChangePasswordFields>;
  formAction: (payload: ChangePasswordFields) => void;
  isPhone: boolean;
  username: string;
  sudoPassword: string;
}) {
  return (
    <FrontPage isPhone={isPhone} showLanguageSelector>
      <ChangePassword
        state={state}
        formAction={formAction}
        isPhone={isPhone}
        username={username}
        sudoPassword={sudoPassword}
      />
    </FrontPage>
  );
}
